import {
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField
} from '@material-ui/core'
import React from 'react'

import {
  cancelAtHomePlan,
  fetchAtHomePlans,
  renewAtHomePlan,
  resendAtHomePlanLink
} from 'api/atHome'
import Button from 'app/components/generic/Button'
import constants from 'app/constants'
import { confirm, eventAlert } from 'app/helpers'

import { planRow } from './format'
import PlanSessionsDialog from './PlanSessionsDialog'
import AtHomeCrudPage from './shared/AtHomeCrudPage'
import { parentLinkOptions } from './shared/parentLink'

// Same shape as the Trial tab (owner, 2026-10-21): when it was booked, then
// who (category, teacher, parent, child), then the classes, then payment and
// where it stands. Parent name and phone open the parent's Alfred profile.
const columnsFor = (rowsRef) => [
  { name: 'bookedOnLabel', label: 'Booked on', options: { keepOrder: true } },
  { name: 'bookedAtLabel', label: 'Booked at', options: { keepOrder: true } },
  { name: 'categoryName', label: 'Subject' },
  { name: 'teacherLabel', label: 'Teacher' },
  {
    name: 'parentName',
    label: 'Parent',
    options: parentLinkOptions(rowsRef, 'parentName')
  },
  {
    name: 'parentPhone',
    label: "Parent's phone",
    options: parentLinkOptions(rowsRef, 'parentPhone')
  },
  { name: 'childName', label: 'Child' },
  { name: 'childGradeLabel', label: "Child's grade" },
  { name: 'schoolName', label: 'School' },
  { name: 'kindLabel', label: 'Project or monthly' },
  { name: 'moduleLabel', label: 'Project' },
  { name: 'frequencyLabel', label: 'Classes a week' },
  { name: 'scheduleLabel', label: 'Day & time', options: { keepOrder: true } },
  {
    name: 'datesLabel',
    label: 'First to last class',
    options: { keepOrder: true }
  },
  {
    name: 'progressLabel',
    label: 'Classes marked done',
    options: { keepOrder: true }
  },
  { name: 'priceLabel', label: 'Charged (incl. GST)' },
  { name: 'paymentLabel', label: 'Payment' },
  // Pending / Booked / Completed / Cancelled — the list is fetched with
  // status=all, so without this column a cancelled plan looks live.
  { name: 'statusLabel', label: 'Where it stands' }
]

/** Bookings → Regular: every Project and Monthly booking, with its sessions summarised. */
const AtHomePlans = () => {
  const [cancelling, setCancelling] = React.useState(null)
  const [viewing, setViewing] = React.useState(null)
  const [reason, setReason] = React.useState('')
  const [refreshKey, setRefreshKey] = React.useState(0)
  // The rows as drawn, for the parent-link cells.
  const rowsRef = React.useRef([])

  const fetchList = React.useCallback(async () => {
    const list = (await fetchAtHomePlans({ status: 'all' })).map(planRow)
    rowsRef.current = list
    return list
  }, [])

  const act = async (fn, okMsg) => {
    try {
      await fn()
      eventAlert(okMsg, constants.SUCCESS)
      setRefreshKey((k) => k + 1)
    } catch (error) {
      // toast from request.js
    }
  }

  return (
    <>
      <AtHomeCrudPage
        subtitle="Projects and monthly classes booked. Open a row to move or cancel one class."
        entityLabel="Booking"
        fetchList={fetchList}
        columns={columnsFor(rowsRef)}
        refreshKey={refreshKey}
        rowActions={(row) =>
          row.status === 'booked' ? (
            <>
              <Button size="small" onClick={() => setViewing(row)}>
                sessions
              </Button>
              {row.payment_status === 'link_sent' && (
                <Button
                  size="small"
                  onClick={() =>
                    act(
                      () => resendAtHomePlanLink(row.uuid),
                      'Payment link sent again on WhatsApp'
                    )
                  }
                >
                  resend payment link
                </Button>
              )}
              {row.kind === 'monthly' && (
                <Button
                  size="small"
                  onClick={() =>
                    confirm(
                      () =>
                        act(
                          () => renewAtHomePlan(row.uuid),
                          'Payment link for next month sent on WhatsApp'
                        ),
                      `Book ${row.childName}'s next month of ${
                        row.categoryName
                      } from the day after ${
                        row.datesLabel.split(' – ')[1]
                      }, same days and teacher? The parent gets a payment link; nothing is booked until they pay.`,
                      undefined,
                      { confirmButtonText: 'Book next month' }
                    )
                  }
                >
                  book next month
                </Button>
              )}
              <Button size="small" onClick={() => setCancelling(row)}>
                cancel
              </Button>
            </>
          ) : null
        }
      />
      <PlanSessionsDialog
        plan={viewing}
        open={Boolean(viewing)}
        onClose={() => setViewing(null)}
        onChanged={() => setRefreshKey((k) => k + 1)}
      />
      <Dialog
        open={Boolean(cancelling)}
        onClose={() => setCancelling(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Cancel this booking?</DialogTitle>
        <DialogContent>
          Every class that has not happened yet is cancelled, and that weekly
          time opens up again for other parents.
          <TextField
            label="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setCancelling(null)}>Keep</MuiButton>
          <MuiButton
            onClick={() =>
              act(
                () => cancelAtHomePlan(cancelling.uuid, reason),
                'Booking cancelled'
              ).then(() => {
                setCancelling(null)
                setReason('')
              })
            }
            color="secondary"
            variant="contained"
          >
            Cancel booking
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default AtHomePlans
